var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Signaling } from "./Signaling";
import { Logger } from "../../Logger";
import { LoggableMethodFactory } from "../../log.decorator";
import { debugLogMajorEvent } from "../../DebugLogger";
import { VideoPerformanceStages, videoPerformanceTracker } from "../../videoPerformance";
import { gatherVideoClientDataSource, metricsManager as globalsMetricsManager, } from "../../Globals";
import { GatedFeatName } from "gather-client-common/dist/src/public/gatekeeping/FEATURES";
import { routerConnState } from "./RouterConnectionStateMachine";
import { createTrackedInterpreter } from "./StateMachineUtils";
import { ConnectionAttemptCounter, getReloadDelayMs } from "./reconnectUtils";
import { ErrorContext } from "gather-common/dist/src/public/utils";
import { GVCInternalEvent } from "../../EventEmitter/GVCEventEmitter";
import { WebSocketConnectionState, WebSocketDisconnectReason, } from "../../EventEmitter/WebSocketEvents";
const LoggableMethod = LoggableMethodFactory("RouterClient");
export default class RouterClient {
    id;
    spaceId;
    routerURLs = [];
    externalEventEmitter;
    selectedRouterURL = "";
    _router;
    cleanupFns = [];
    shouldRetainSFUAssignment;
    ownSfuData;
    connectionAttemptCounter;
    routerReconnectTimer;
    routerConnStateService;
    useSfuScoring;
    constructor(id, spaceId, routerURLs, eventEmitter, shouldRetainSFUAssignment, useSfuScoring) {
        this.id = id;
        this.routerURLs = routerURLs;
        this.spaceId = spaceId;
        this.externalEventEmitter = eventEmitter;
        this.shouldRetainSFUAssignment = shouldRetainSFUAssignment;
        this.useSfuScoring = useSfuScoring;
        this.routerConnStateService = createTrackedInterpreter(routerConnState);
        this.routerConnStateService.start();
        this.connectionAttemptCounter = new ConnectionAttemptCounter();
        this.connectionAttemptCounter.recordAttempt();
        this._router = this.createSignaling();
    }
    _webSocketConnectionStateChanged = (state, context) => {
        switch (state) {
            case WebSocketConnectionState.Connected: {
                Logger.log(`Router ${this.selectedRouterURL} is connected`);
                this.routerConnStateService.send("CONNECT");
                this.externalEventEmitter.publishEvent(GVCInternalEvent.RouterConnected);
                break;
            }
            case WebSocketConnectionState.Disconnected: {
                const { reason } = context;
                if (!reason) {
                    Logger.error("WebSocketConnectionState.Disconnected without a reason");
                    return;
                }
                Logger.log(`Router at ${this.selectedRouterURL} is disconnected: ${reason}`);
                this.routerConnStateService.send({ type: "DISCONNECT", reason });
                this.externalEventEmitter.publishEvent(GVCInternalEvent.RouterDisconnected);
                this.routerReconnectWithDelay();
                if (reason === WebSocketDisconnectReason.Unknown) {
                    Logger.error("Unknown WebSocketDisconnectReason");
                }
            }
        }
    };
    createSignaling(optionsOverride = {}) {
        this.selectedRouterURL = this.getNewRouterURL();
        const router = new Signaling(this.selectedRouterURL, this.spaceId, this.id, {
            useGlobalAccelerator: true,
            useSfuScoring: this.useSfuScoring,
            ...optionsOverride,
        });
        this.cleanupFns = [
            router.addEventListener(GVCInternalEvent.WebSocketConnectionStateChange, this._webSocketConnectionStateChanged, { leading: true }),
        ];
        router.on("addrs", this.onAddrs.bind(this));
        router.on("cordon-sfu", this.onCordonSFU.bind(this));
        this._router = router;
        return router;
    }
    getNewRouterURL() {
        const url = this.routerURLs[Math.floor(Math.random() * this.routerURLs.length)];
        if (url)
            return url;
        throw new Error("No routers available for connection");
    }
    routerReconnectWithDelay() {
        if (!this.routerReconnectTimer) {
            const reloadDelay = getReloadDelayMs(this.connectionAttemptCounter.attemptsInLast30s);
            if (process.env.ENVIRONMENT !== "local" || process.env.ROUTER_RETRY === "true") {
                Logger.warn(`reconnect to router in ${reloadDelay}ms`);
                this.routerReconnectTimer = window.setTimeout(() => {
                    this.routerReconnect();
                }, reloadDelay);
            }
        }
    }
    routerReconnect() {
        const signalingOptions = {
            retainSFUAssignment: this.shouldRetainSFUAssignment(),
            sfuAddr: this.ownSfuData?.addr,
        };
        this.reconnectWithOptions(signalingOptions);
    }
    reconnectWithOptions(options) {
        delete this.routerReconnectTimer;
        this.connectionAttemptCounter.recordAttempt();
        this.cleanupFns.forEach((f) => f());
        this._router?.destroy();
        this.createSignaling(options);
        Logger.log(`Reconnect to router at ${this.selectedRouterURL} with options ${JSON.stringify(options)}`);
    }
    sendGetAddrs(id) {
        this._router
            .sendWithResponse("get-addr", { srcId: id, srcStreamId: this.spaceId })
            .then(({ addrFound }) => {
            if (!addrFound) {
                debugLogMajorEvent(`Router couldn't find addr for ${id}`);
            }
        })
            .catch((err) => {
            Logger.error(err, new ErrorContext({
                srcId: id,
                ...this.getConnectionReport(),
            }));
        });
    }
    sendUnsubscribe(id) {
        this._router.emit("unsubscribe", { srcId: id, srcStreamId: this.spaceId });
    }
    requestServerDebug(userIds) {
        const spaceId = this.spaceId;
        const msg = { srcIds: userIds, srcStreamId: spaceId };
        this._router.emit("debug-router", msg);
    }
    onAddrs(addr) {
        this._measureRouterFetchDuration(addr.srcId);
        if (addr.srcId === this.id) {
            this.ownSfuData = { addr: addr.sfuAddr };
        }
        this.externalEventEmitter.publishEvent(GVCInternalEvent.RouterAddrs, addr);
    }
    onCordonSFU({ sfuAddr }) {
        if (sfuAddr === this.ownSfuData?.addr) {
            this.ownSfuData.status = "cordoned";
            this.externalEventEmitter.publishEvent(GVCInternalEvent.RouterCordonSFU, { sfuAddr });
        }
    }
    _measureRouterFetchDuration(srcId) {
        const routerFetchMeasure = videoPerformanceTracker.measureStatic("routerFetchTime", VideoPerformanceStages.RouterGetAddr, VideoPerformanceStages.RouterSetAddr, srcId);
        if (routerFetchMeasure) {
            globalsMetricsManager.addMetricReliably("avLoadTime", {
                routerFetchTime: routerFetchMeasure.duration,
            });
        }
    }
    sendRemoteLog(message) {
        if (gatherVideoClientDataSource.gate(GatedFeatName.RemoteLogsEnabled, this.id, this.spaceId)) {
            this._router.emit("remote-log", {
                message,
            });
        }
    }
    async moveSFU() {
        let succeed = false;
        let newSfu = "";
        if (!this._router) {
            Logger.error("Have no Router Signaling, can't reassign");
            return { succeed: false, newSfu };
        }
        delete this.ownSfuData?.status;
        try {
            const resp = await this._router?.sendWithResponse("reassign");
            succeed = resp?.assignmentChanged || false;
            newSfu = resp?.newSfu || "";
        }
        catch (error) {
            Logger.error(`reassign failed`, new ErrorContext({
                oldSfu: this.ownSfuData?.addr || "None",
                status: this.ownSfuData?.status || "None",
            }, error));
        }
        return { succeed, newSfu };
    }
    summarize() {
        return {
            routerURL: this.selectedRouterURL,
            routerConnection: this._router?._summarize(),
        };
    }
    getConnectionReport() {
        return this._router.getConnectionReport();
    }
    getOwnSfuStatus() {
        return this.ownSfuData?.status;
    }
    destroy() {
        if (this.routerReconnectTimer) {
            window.clearTimeout(this.routerReconnectTimer);
        }
        this.cleanupFns.forEach((f) => f());
        this._router.destroy();
    }
    _DEBUG_assignSfu(sfu) {
        this.reconnectWithOptions({
            retainSFUAssignment: true,
            sfuAddr: sfu,
        });
    }
}
__decorate([
    LoggableMethod
], RouterClient.prototype, "routerReconnectWithDelay", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "routerReconnect", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "sendGetAddrs", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "sendUnsubscribe", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "requestServerDebug", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "_measureRouterFetchDuration", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "sendRemoteLog", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "moveSFU", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "summarize", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "destroy", null);
__decorate([
    LoggableMethod
], RouterClient.prototype, "_DEBUG_assignSfu", null);
//# sourceMappingURL=RouterClient.js.map